import {useState} from 'react'
import dayjs from 'dayjs'
import {useTranslation} from '@/translations/i18n.tsx'
import {ChevronLeftIcon, ChevronRightIcon} from '@/components/ui/icon/Icon'
import {Button} from '@/components/ui/button/Button'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    ModalTitle,
    ModalXCloseButton
} from '@/components/ui/modal-atoms/ModalAtoms'
import {BATCH_DETAIL_PAGE_SIZE} from '@/features/mailer/constants'
import {MailBatchSummary, MailLogStatus} from '@/features/mailer/types'
import {useMailBatchDetail} from '@/features/mailer/queries/useMailBatchDetail'
import {
    StyledKindBadge,
    StyledLogStatusBadge,
    StyledModalEmptyState,
    StyledModalHeaderTexts,
    StyledModalPagination,
    StyledModalTab,
    StyledModalTabCounter,
    StyledModalTable,
    StyledModalTableWrapper,
    StyledModalTabs
} from '@/features/mailer/components/mailer-batch-details-modal/style'

type ModalTab = 'all' | MailLogStatus

interface MailerBatchDetailsModalProps {
    batch: MailBatchSummary
    onClose: () => void
}

const KIND_LABEL_KEYS = {
    patient: 'mailer:kind_patient',
    consultant: 'mailer:kind_consultant',
    whitelist: 'mailer:kind_whitelist'
} as const

const LOG_STATUS_LABEL_KEYS = {
    sent: 'mailer:log_status_sent',
    failed: 'mailer:log_status_failed',
    bounced: 'mailer:log_status_bounced',
    pending: 'mailer:log_status_pending'
} as const

// Per-recipient delivery detail (design §5.12 / spec §10.1): status tabs with
// counters from the batch summary, 6-column table, server pagination 8/page.
export const MailerBatchDetailsModal = ({batch, onClose}: MailerBatchDetailsModalProps) => {
    const {t} = useTranslation()
    const [activeTab, setActiveTab] = useState<ModalTab>('all')
    const [page, setPage] = useState(1)

    const detailQuery = useMailBatchDetail({
        batchId: batch.batchId,
        page,
        limit: BATCH_DETAIL_PAGE_SIZE,
        status: activeTab === 'all' ? undefined : activeTab
    })

    const rows = detailQuery.data?.data ?? []
    const meta = detailQuery.data?.meta

    const tabs: Array<{value: ModalTab; label: string; count: number}> = [
        {value: 'all', label: t('mailer:batch_detail_filter_all'), count: batch.totalRecipients},
        {value: 'sent', label: t('mailer:batch_detail_filter_sent'), count: batch.counts.sent},
        {value: 'failed', label: t('mailer:batch_detail_filter_failed'), count: batch.counts.failed},
        {value: 'bounced', label: t('mailer:batch_detail_filter_bounced'), count: batch.counts.bounced},
        {value: 'pending', label: t('mailer:batch_detail_filter_pending'), count: batch.counts.pending}
    ]

    const selectTab = (tab: ModalTab) => {
        setActiveTab(tab)
        setPage(1)
    }

    return (
        <>
            <ModalOverlay onClick={onClose} />
            <Modal width="768px" role="dialog" aria-modal>
                <ModalHeader>
                    <StyledModalHeaderTexts>
                        <small>
                            {t('mailer:batch_detail_subtitle', {
                                date: dayjs(batch.createdAt).format('DD/MM/YYYY HH:mm')
                            })}
                        </small>
                        <ModalTitle>{batch.subject}</ModalTitle>
                        <p>{t('mailer:batch_detail_recipients_total', {count: batch.totalRecipients})}</p>
                    </StyledModalHeaderTexts>
                    <ModalXCloseButton aria-label={t('mailer:close')} onClick={onClose} />
                </ModalHeader>

                <StyledModalTabs>
                    {tabs.map(tab => (
                        <StyledModalTab
                            key={tab.value}
                            type="button"
                            $active={activeTab === tab.value}
                            onClick={() => selectTab(tab.value)}
                        >
                            {tab.label}
                            <StyledModalTabCounter $active={activeTab === tab.value}>{tab.count}</StyledModalTabCounter>
                        </StyledModalTab>
                    ))}
                </StyledModalTabs>

                <ModalBody>
                    <StyledModalTableWrapper>
                        {detailQuery.isLoading ? (
                            <Flexbox justify="center" style={{padding: 32}}>
                                <Spinner size={24} />
                            </Flexbox>
                        ) : rows.length === 0 ? (
                            <StyledModalEmptyState>{t('mailer:batch_detail_empty_for_tab')}</StyledModalEmptyState>
                        ) : (
                            <StyledModalTable>
                                <thead>
                                    <tr>
                                        <th>{t('mailer:batch_detail_column_recipient')}</th>
                                        <th>{t('mailer:batch_detail_column_kind')}</th>
                                        <th>{t('mailer:batch_detail_column_status')}</th>
                                        <th className="centered">{t('mailer:batch_detail_column_attempts')}</th>
                                        <th>{t('mailer:batch_detail_column_sent_at')}</th>
                                        <th>{t('mailer:batch_detail_column_error')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map(log => (
                                        <tr key={log.id}>
                                            <td>
                                                <strong>{log.recipient}</strong>
                                            </td>
                                            <td>
                                                <StyledKindBadge $kind={log.recipientKind}>
                                                    {t(KIND_LABEL_KEYS[log.recipientKind])}
                                                </StyledKindBadge>
                                            </td>
                                            <td>
                                                <StyledLogStatusBadge $status={log.status}>
                                                    {t(LOG_STATUS_LABEL_KEYS[log.status])}
                                                </StyledLogStatusBadge>
                                            </td>
                                            <td className="centered">{log.attempts}</td>
                                            <td>{log.sentAt ? dayjs(log.sentAt).format('DD/MM/YYYY HH:mm') : '—'}</td>
                                            <td className="error-cell">{log.error ?? '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </StyledModalTable>
                        )}
                    </StyledModalTableWrapper>
                </ModalBody>

                <ModalFooter>
                    <StyledModalPagination>
                        <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            shape="square"
                            aria-label={t('mailer:pagination_prev')}
                            disabled={page <= 1}
                            onClick={() => setPage(previous => Math.max(1, previous - 1))}
                        >
                            <ChevronLeftIcon />
                        </Button>
                        <span className="page-label">
                            {t('mailer:batch_detail_pagination_label', {
                                current: meta?.currentPage ?? page,
                                total: meta?.lastPage ?? 1
                            })}
                        </span>
                        <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            shape="square"
                            aria-label={t('mailer:pagination_next')}
                            disabled={!meta || page >= meta.lastPage}
                            onClick={() => setPage(previous => previous + 1)}
                        >
                            <ChevronRightIcon />
                        </Button>
                        {meta && (
                            <span className="results-label">
                                {t('mailer:batch_detail_results_count', {count: meta.total})}
                            </span>
                        )}
                    </StyledModalPagination>
                    <Button type="button" variant="primary" onClick={onClose}>
                        {t('mailer:batch_detail_close_cta')}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    )
}

MailerBatchDetailsModal.displayName = 'MailerBatchDetailsModal'
